import { MORALE_MAP, MORALE_CLASS, SECTION_MAX_UNITS } from "../constants";

export function moraleLabel(val) {
  if (!val) return null;
  return MORALE_MAP[String(val).trim().toLowerCase()] || String(val);
}

export function moraleClass(val) {
  const label = moraleLabel(val);
  return MORALE_CLASS[label] || "";
}

export function findUnit(faction, unitId) {
  if (!faction) return null;
  return faction.units.find((u) => u.id === unitId) || null;
}

// Number of roster lines currently in a given category.
export function categoryUnitCount(faction, roster, category) {
  return roster.filter((e) => {
    const u = findUnit(faction, e.unitId);
    return u && u.category === category;
  }).length;
}

// True when a category has reached its section-level unit cap (if any).
export function isCategoryFull(faction, roster, category) {
  const max = SECTION_MAX_UNITS[category];
  if (max == null) return false;
  return categoryUnitCount(faction, roster, category) >= max;
}

export function isAtMax(unit, currentQty) {
  return unit.maxQty != null && currentQty >= unit.maxQty;
}

// Small badge text describing a unit's qty constraints.
export function qtyRangeLabel(unit, currentQty) {
  if (unit.minQty == null && unit.maxQty == null) return "";
  if (isAtMax(unit, currentQty)) return `Max ${unit.maxQty} reached`;
  if (unit.minQty != null && unit.maxQty != null) return `Qty ${unit.minQty}\u2013${unit.maxQty}`;
  if (unit.minQty != null) return `Min ${unit.minQty}`;
  return `Max ${unit.maxQty}`;
}

// Sum a numeric field across selected entries in `list`.
export function sumSelected(list, selectedIds, field) {
  if (!list || !list.length) return 0;
  return list.reduce((sum, item) => sum + (selectedIds.includes(item.id) ? (item[field] || 0) : 0), 0);
}

export function lineTotal(faction, entry) {
  const unit = findUnit(faction, entry.unitId);
  if (!unit) return 0;
  let per = unit.points;
  per += sumSelected(unit.upgrades, entry.upgrades, "points");
  per += sumSelected(unit.optionalSpecialRules, entry.optionalSpecialRules, "points");
  per += sumSelected(unit.options, entry.options, "points");
  let total = per * entry.qty;
  // "pointsPerQty" options scale with the unit's current effective quantity
  // (effective composition model count x line qty), combined with any flat points.
  total += sumSelected(unit.options, entry.options, "pointsPerQty") * effectiveQuantity(unit, entry);
  return total;
}

export function lineBrPerCopy(unit, entry) {
  let br = unit.br || 0;
  br += sumSelected(unit.optionalSpecialRules, entry.optionalSpecialRules, "br");
  br += sumSelected(unit.options, entry.options, "br");
  return br;
}

// Effective morale: base, overridden by selected special rule, then selected option.
export function effectiveMorale(unit, entry) {
  let m = unit.morale || null;
  (unit.optionalSpecialRules || []).forEach((r) => {
    if (entry.optionalSpecialRules.includes(r.id) && r.morale) m = r.morale;
  });
  (unit.options || []).forEach((o) => {
    if (entry.options.includes(o.id) && o.morale) m = o.morale;
  });
  return m;
}

// Effective composition: base list, then each selected option's composition
// merged in order. Merge behaviour is driven per composition ENTRY via its
// "mode" (with an option-level "compositionMode" still honoured as a fallback):
//   - "append": the entry is added alongside the existing entries
//   - "modify": the target entry's qty/name is updated in place. The target is
//       the entry named by "replaces" (else the first entry). qty becomes the
//       entry's qty (or the target's current qty) plus any "qtyChange"; the name
//       becomes the entry's name when given, otherwise the target's name.
//   - no mode (and no option compositionMode): the list is replaced entirely
// Output entries are always {qty, name} so the display never shows undefined.
export function effectiveComposition(unit, entry) {
  const clean = (c) => ({ qty: c.qty, name: c.name });
  let comp = (unit.unitComposition || []).slice();

  (unit.options || []).forEach((o) => {
    if (!entry.options.includes(o.id) || !o.composition) return;

    if (o.compositionMode === "replace") {
      comp = o.composition.map(clean);
      return;
    }
    if (o.compositionMode === "append") {
      comp = comp.concat(o.composition.map(clean));
      return;
    }

    const hasEntryMode = o.composition.some((c) => c && (c.mode || c.qtyChange != null));
    if (!hasEntryMode && o.compositionMode == null) {
      comp = o.composition.map(clean);
      return;
    }

    o.composition.forEach((c) => {
      if (!c) return;
      const mode = c.mode || (c.qtyChange != null ? "modify" : "append");
      if (mode === "modify") {
        if (comp.length === 0) return;
        let idx = 0;
        if (c.replaces != null && !Array.isArray(c.replaces)) {
          const found = comp.findIndex((x) => x.name === c.replaces);
          if (found >= 0) idx = found;
        }
        const base = comp[idx];
        const newQty = (Number(c.qty != null ? c.qty : base.qty) || 0) + (Number(c.qtyChange) || 0);
        comp = comp.map((x, i) =>
          i === idx ? { qty: newQty, name: c.name != null ? c.name : x.name } : x
        );
      } else if (mode === "replace" && Array.isArray(c.replaces)) {
        // Remove every unit named in the replaces array; if keepQty, the new
        // entry inherits the summed quantity of everything it replaced.
        const removedQty = comp
          .filter((x) => c.replaces.includes(x.name))
          .reduce((s, x) => s + (Number(x.qty) || 0), 0);
        comp = comp.filter((x) => !c.replaces.includes(x.name));
        const newQty = c.keepQty ? removedQty : (Number(c.qty) || 0);
        comp = comp.concat([{ qty: newQty, name: c.name }]);
      } else {
        // append — an explicit mode:"append" entry merges into an existing
        // same-named entry so quantities from multiple groups aggregate into one.
        const idx = c.mode === "append" ? comp.findIndex((x) => x.name === c.name) : -1;
        if (idx >= 0) {
          comp = comp.map((x, i) =>
            i === idx ? { qty: (Number(x.qty) || 0) + (Number(c.qty) || 0), name: x.name } : x
          );
        } else {
          comp = comp.concat([clean(c)]);
        }
      }
    });
  });

  return comp;
}

// Effective unit quantity: total model count across the current effective
// composition, multiplied by the roster line quantity. Updates live as
// composition-changing options or the qty stepper change.
export function effectiveQuantity(unit, entry) {
  const perCopy = effectiveComposition(unit, entry).reduce((s, c) => s + (Number(c.qty) || 0), 0);
  return perCopy * entry.qty;
}

// Normalize a special-rules field that may be a string, an array, or absent.
export function normalizeRules(val) {
  if (!val) return [];
  return Array.isArray(val) ? val : [val];
}

// Effective special rules: base, with selected options' remove/add applied in order.
export function effectiveSpecialRules(unit, entry) {
  let rules = (unit.specialRules || []).slice();
  const selected = entry.options || [];
  (unit.options || []).forEach((o) => {
    if (!selected.includes(o.id)) return;
    const removeRules = normalizeRules(o.removeSpecialRules);
    if (removeRules.length) {
      const removeNames = removeRules.map((n) => String(n).toLowerCase());
      rules = rules.filter((r) => {
        const name = (typeof r === "string" ? r : r.name).toLowerCase();
        return !removeNames.includes(name);
      });
    }
    const addRules = normalizeRules(o.addSpecialRules);
    if (addRules.length) {
      rules = rules.concat(addRules);
    }
    // Option-level specialRules object: { mode: append|replace|modify, rules: [...], replaces? }
    const sr = o.specialRules;
    if (sr && typeof sr === "object" && Array.isArray(sr.rules)) {
      if (sr.mode === "replace") {
        rules = sr.rules.slice();
      } else if (sr.mode === "modify") {
        const target = sr.replaces != null ? String(sr.replaces).toLowerCase() : null;
        const idx = target == null ? -1 : rules.findIndex(
          (r) => (typeof r === "string" ? r : r.name).toLowerCase() === target
        );
        if (idx >= 0) {
          rules = [...rules.slice(0, idx), ...sr.rules, ...rules.slice(idx + 1)];
        } else {
          rules = rules.concat(sr.rules);
        }
      } else {
        // default / "append"
        rules = rules.concat(sr.rules);
      }
    }
  });
  return rules;
}

export function formatComposition(comp) {
  if (!comp || !comp.length) return "";
  return comp.map((c) => `${c.qty}x ${c.name}`).join(", ");
}

// Label suffix for an optionalSpecialRules / options checkbox. effQty is the
// unit's current effective quantity, used to render pointsPerQty as a total.
export function optionLabel(opt, effQty = 0) {
  const parts = [];
  const ptsTotal = (opt.points || 0) + (opt.pointsPerQty || 0) * effQty;
  if (ptsTotal) parts.push(`${ptsTotal > 0 ? "+" : ""}${ptsTotal} pts`);
  if (opt.br) parts.push(`${opt.br > 0 ? "+" : ""}${opt.br} BR`);
  if (opt.morale) parts.push(`morale: ${moraleLabel(opt.morale)}`);
  const addRules = normalizeRules(opt.addSpecialRules);
  if (addRules.length) {
    const names = addRules.map((r) => (typeof r === "string" ? r : r.name)).join(", ");
    parts.push(`+rule: ${names}`);
  }
  const removeRules = normalizeRules(opt.removeSpecialRules);
  if (removeRules.length) {
    parts.push(`\u2212rule: ${removeRules.join(", ")}`);
  }
  return opt.name + (parts.length ? ` (${parts.join(", ")})` : "");
}

export function computeTotals(faction, roster) {
  let pts = 0, br = 0;
  if (faction) {
    roster.forEach((entry) => {
      const unit = findUnit(faction, entry.unitId);
      if (!unit) return;
      pts += lineTotal(faction, entry);
      br += lineBrPerCopy(unit, entry) * entry.qty;
    });
  }
  return { pts, br };
}

function ruleName(r) {
  return (typeof r === "string" ? r : (r && r.name) || "").trim();
}

// Base "Orders" dice pool derived from the army points total.
export function ordersDice(pts) {
  const p = Number(pts) || 0;
  if (p <= 500) return "2d6";
  if (p <= 750) return "3d6";
  return "4d6";
}

// Orders modifier: number of unit copies whose effective special rules
// include "Officer" or "Senior Officer".
export function officerModifier(faction, roster) {
  if (!faction) return 0;
  return roster.reduce((sum, entry) => {
    const unit = findUnit(faction, entry.unitId);
    if (!unit) return sum;
    const hasOfficer = effectiveSpecialRules(unit, entry).some((r) => {
      const n = ruleName(r).toLowerCase();
      return n === "officer" || n === "senior officer";
    });
    return sum + (hasOfficer ? (Number(entry.qty) || 0) : 0);
  }, 0);
}

// Full "Orders" formula string, e.g. "3d6+2" (omits +0).
export function ordersFormula(faction, roster, pts) {
  const mod = officerModifier(faction, roster);
  return ordersDice(pts) + (mod > 0 ? "+" + mod : "");
}

// Scouting total: sums Scout special rules across the whole composition.
// "Scout" = 1, "Scout 2" = 2, "Scout 3" = N, multiplied by unit copies.
export function scoutingTotal(faction, roster) {
  if (!faction) return 0;
  return roster.reduce((sum, entry) => {
    const unit = findUnit(faction, entry.unitId);
    if (!unit) return sum;
    const qty = Number(entry.qty) || 0;
    let s = 0;
    effectiveSpecialRules(unit, entry).forEach((r) => {
      const m = ruleName(r).match(/^scout(?:\s+(\d+))?$/i);
      if (m) s += m[1] ? Number(m[1]) : 1;
    });
    return sum + s * qty;
  }, 0);
}


export function brHintFor(limit) {
  if (!limit) return "";
  const low = Math.max(1, Math.round(limit / 85));
  const high = Math.max(low + 1, Math.round(limit / 55));
  return `typ. ${low}\u2013${high} BR`;
}

export function currentLimit(pointsMode, customLimit) {
  return pointsMode === "custom" ? (Number(customLimit) || 0) : Number(pointsMode);
}

// Maximum number of "restricted" slots allowed for a given total points limit.
export function restrictedLimit(limit) {
  if (!limit || limit <= 0) return Infinity;
  if (limit <= 500) return 2;
  if (limit <= 750) return 3;
  if (limit <= 1000) return 4;
  return 4 + Math.ceil((limit - 1000) / 250);
}

// The composition merge mode of an option (option-level, then entry-level).
function optionMode(o) {
  if (o.compositionMode) return o.compositionMode;
  const comp = o.composition || [];
  for (const c of comp) {
    if (c && c.mode) return c.mode;
    if (c && c.qtyChange != null) return "modify";
  }
  return "replace";
}

// Restricted slots consumed by a single roster entry (multiplied by the entry's
// quantity — each instance of a restricted unit / restricted option = 1 slot).
//  - a restricted unit = 1 slot per copy
//  - a restricted replace/modify option adds no extra slot (unit still 1/copy)
//  - a restricted append option adds +1/copy only if the parent unit is also
//    restricted; otherwise it just makes the unit count as 1 slot/copy total
export function restrictedSlotsForEntry(unit, entry) {
  if (!unit) return 0;
  const unitRestricted = unit.restricted === "yes";
  const selected = [
    ...(unit.upgrades || []).filter((x) => entry.upgrades.includes(x.id)),
    ...(unit.optionalSpecialRules || []).filter((x) => entry.optionalSpecialRules.includes(x.id)),
    ...(unit.options || []).filter((x) => entry.options.includes(x.id)),
  ].filter((o) => o.restricted === "yes");

  let slots = unitRestricted ? 1 : 0;
  let floorOne = unitRestricted;
  let extraAppend = 0;
  selected.forEach((o) => {
    if (optionMode(o) === "append" && unitRestricted) {
      extraAppend += 1;
    } else {
      floorOne = true;
    }
  });
  if (floorOne) slots = Math.max(slots, 1);
  return (slots + extraAppend) * (Number(entry.qty) || 0);
}

export function countRestricted(faction, roster) {
  if (!faction) return 0;
  return roster.reduce((sum, entry) => sum + restrictedSlotsForEntry(findUnit(faction, entry.unitId), entry), 0);
}

// Set of unit IDs currently unlocked by units present in the roster.
export function computeUnlocked(faction, roster) {
  const set = new Set();
  if (!faction) return set;
  roster.forEach((e) => {
    const u = findUnit(faction, e.unitId);
    if (u && Array.isArray(u.unlocks)) u.unlocks.forEach((id) => set.add(id));
  });
  return set;
}

// A unit is visible unless it is hiddenUntilUnlocked and not currently unlocked.
export function isUnitVisible(unit, unlockedSet) {
  if (!unit) return false;
  if (unit.hiddenUntilUnlocked) return unlockedSet.has(unit.id);
  return true;
}

// Remove roster entries that are hiddenUntilUnlocked but no longer unlocked
// (e.g. after their unlocking unit was removed). Iterates to handle chains.
export function pruneLocked(faction, roster) {
  if (!faction) return roster;
  let current = roster;
  for (let i = 0; i < 10; i++) {
    const unlocked = computeUnlocked(faction, current);
    const next = current.filter((e) => {
      const u = findUnit(faction, e.unitId);
      return !(u && u.hiddenUntilUnlocked && !unlocked.has(u.id));
    });
    if (next.length === current.length) return next;
    current = next;
  }
  return current;
}

// Look up an equipment record by id in the army JSON's equipment list.
export function findEquipment(faction, equipmentId) {
  if (!faction || !Array.isArray(faction.equipment)) return null;
  return faction.equipment.find((e) => e.equipmentId === equipmentId) || null;
}

// Read-only resolution of the final composition that PRESERVES equipmentId.
// Mirrors effectiveComposition's merge rules exactly (does not modify it), so
// the PDF equipment stats reflect the final composition after all selected
// options are applied (e.g. a modify option that replaces the base entry
// yields only the replacement's equipmentId, never both).
export function resolvedComposition(unit, entry) {
  if (!unit) return [];
  const clean = (c) => ({ qty: c.qty, name: c.name, equipmentId: c.equipmentId });
  let comp = (unit.unitComposition || []).map(clean);

  (unit.options || []).forEach((o) => {
    if (!entry.options.includes(o.id) || !o.composition) return;

    if (o.compositionMode === "replace") { comp = o.composition.map(clean); return; }
    if (o.compositionMode === "append") { comp = comp.concat(o.composition.map(clean)); return; }

    const hasEntryMode = o.composition.some((c) => c && (c.mode || c.qtyChange != null));
    if (!hasEntryMode && o.compositionMode == null) { comp = o.composition.map(clean); return; }

    o.composition.forEach((c) => {
      if (!c) return;
      const mode = c.mode || (c.qtyChange != null ? "modify" : "append");
      if (mode === "modify") {
        if (comp.length === 0) return;
        let idx = 0;
        if (c.replaces != null && !Array.isArray(c.replaces)) {
          const found = comp.findIndex((x) => x.name === c.replaces);
          if (found >= 0) idx = found;
        }
        const base = comp[idx];
        const newQty = (Number(c.qty != null ? c.qty : base.qty) || 0) + (Number(c.qtyChange) || 0);
        comp = comp.map((x, i) =>
          i === idx
            ? {
                qty: newQty,
                name: c.name != null ? c.name : x.name,
                equipmentId: c.equipmentId != null ? c.equipmentId : x.equipmentId,
              }
            : x
        );
      } else if (mode === "replace" && Array.isArray(c.replaces)) {
        const removedQty = comp
          .filter((x) => c.replaces.includes(x.name))
          .reduce((s, x) => s + (Number(x.qty) || 0), 0);
        comp = comp.filter((x) => !c.replaces.includes(x.name));
        const newQty = c.keepQty ? removedQty : (Number(c.qty) || 0);
        comp = comp.concat([{ qty: newQty, name: c.name, equipmentId: c.equipmentId }]);
      } else {
        const idx = c.mode === "append" ? comp.findIndex((x) => x.name === c.name) : -1;
        if (idx >= 0) {
          comp = comp.map((x, i) =>
            i === idx ? { qty: (Number(x.qty) || 0) + (Number(c.qty) || 0), name: x.name, equipmentId: x.equipmentId } : x
          );
        } else {
          comp = comp.concat([clean(c)]);
        }
      }
    });
  });

  return comp;
}

// Distinct equipment records referenced by a unit entry's FINAL resolved
// composition. Read-only; does not touch the composition merge logic.
export function unitEquipment(faction, unit, entry) {
  if (!unit) return [];
  const seen = new Set();
  const out = [];
  resolvedComposition(unit, entry).forEach((c) => {
    if (!c.equipmentId || seen.has(c.equipmentId)) return;
    seen.add(c.equipmentId);
    const eq = findEquipment(faction, c.equipmentId);
    if (eq) out.push(eq);
  });
  return out;
}

const AMMO_WEIGHTS = ["Very Light", "Light", "Medium", "Heavy", "Very Heavy"];
const isRangeToken = (t) => t === "" || t === "-" || /^-?\d+$/.test(t);

// Parse one ammo string (e.g. "Light, 1,1,1,1,-") into { type, he, ranges[5] }.
export function parseAmmoRow(type, value) {
  const tokens = String(value).split(",").map((t) => t.trim());
  let he = "";
  let ranges = tokens;
  if (tokens.length && !isRangeToken(tokens[0])) {
    if (AMMO_WEIGHTS.some((w) => w.toLowerCase() === tokens[0].toLowerCase())) he = tokens[0];
    ranges = tokens.slice(1);
  }
  ranges = ranges.slice(0, 5);
  while (ranges.length < 5) ranges.push("");
  return { type, he, ranges };
}

export function equipmentName(eq) {
  return eq.vehicleName || eq.helicopterName || eq.aircraftName || eq.infantryName || eq.weaponName || eq.name || "";
}

// True if an option id is selected in any of a roster entry's selection lists.
function isOptionSelected(entry, id) {
  return (
    (entry.upgrades || []).includes(id) ||
    (entry.optionalSpecialRules || []).includes(id) ||
    (entry.options || []).includes(id)
  );
}

// Normalizes a unit's option groups into an array. Prefers the plural
// `optionGroupLimits`; falls back to the singular `optionGroupLimit` (wrapped
// as a single-entry array if it isn't already one). Each group's option-id
// list may be under `ids` or `options`.
export function getOptionGroups(unit) {
  if (!unit) return [];
  let groups = unit.optionGroupLimits != null ? unit.optionGroupLimits : unit.optionGroupLimit;
  if (groups == null) return [];
  if (!Array.isArray(groups)) groups = [groups];
  return groups
    .filter(Boolean)
    .map((g) => ({ ...g, ids: Array.isArray(g.ids) ? g.ids : (Array.isArray(g.options) ? g.options : []) }));
}

// Option dependencies: an option may require other options (by ID) to be
// selected before it becomes available. `requiresAny` needs at least one of the
// listed IDs selected; `requiresAll` needs every listed ID selected. Returns
// true when a declared dependency is unmet (option should be shown dimmed and
// unselectable). IDs are used (not names) to avoid cross-group name clashes.
export function optionDependencyUnmet(item, entry) {
  if (!item || !entry) return false;
  const any = Array.isArray(item.requiresAny) ? item.requiresAny : null;
  const all = Array.isArray(item.requiresAll) ? item.requiresAll : null;
  if (any && any.length && !any.some((id) => isOptionSelected(entry, id))) return true;
  if (all && all.length && !all.every((id) => isOptionSelected(entry, id))) return true;
  return false;
}

// Cross-card option cap: an option's `instanceLimit` caps how many cards of the
// same unit type may have it selected at once. Once that many sibling cards have
// it selected, the option is disabled on the remaining (unselected) cards.
// Already-selected cards stay enabled so the option can still be unchecked.
export function optionInstanceLimitReached(item, entry, siblings) {
  if (!item || item.instanceLimit == null || !Array.isArray(siblings)) return false;
  if (isOptionSelected(entry, item.id)) return false;
  const count = siblings.filter((s) => isOptionSelected(s, item.id)).length;
  return count >= item.instanceLimit;
}

// Required option groups: for each optionGroupLimits entry with min > 0 that has
// fewer than `min` selections, returns the group so a "required selection" warning
// can be shown. A group is skipped while ALL of its options have unmet
// requiresAny/requiresAll dependencies (i.e. none are yet selectable).
export function unmetOptionGroups(unit, entry) {
  if (!unit) return [];
  const groups = getOptionGroups(unit);
  const byId = {};
  (unit.options || []).forEach((o) => { byId[o.id] = o; });
  const result = [];
  for (const g of groups) {
    if (!(g.min > 0)) continue;
    const selectedCount = g.ids.filter((id) => isOptionSelected(entry, id)).length;
    if (selectedCount >= g.min) continue;
    const anyActive = g.ids.some((id) => {
      const item = byId[id];
      return item && !optionDependencyUnmet(item, entry);
    });
    if (!anyActive) continue;
    result.push(g);
  }
  return result;
}

// Unit-level option groups: groups of option ids capped at "max" selections.
// When a group's selected count reaches max, its remaining unselected options
// are disabled (selected ones stay enabled so they can be unchecked).
export function optionGroupDisabled(unit, entry, itemId) {
  const groups = getOptionGroups(unit);
  for (const g of groups) {
    if (!g.ids.includes(itemId)) continue;
    if (g.max == null) continue;
    const selectedCount = g.ids.filter((id) => isOptionSelected(entry, id)).length;
    if (selectedCount >= g.max && !isOptionSelected(entry, itemId)) return true;
  }
  return false;
}

// The reinforcement unit to pre-populate. Faction JSONs prefix ids (e.g.
// "uk-northag-orr-reinforcement-request"), so match the exact id first, then
// fall back to any id ending in "-reinforcement-request" (excluding the
// "additional" variant).
export const REINFORCEMENT_UNIT_ID = "reinforcement-request";
export function findReinforcementUnit(faction) {
  if (!faction) return null;
  return (
    faction.units.find((u) => u.id === REINFORCEMENT_UNIT_ID) ||
    faction.units.find((u) => u.id.endsWith("-" + REINFORCEMENT_UNIT_ID) && !u.id.includes("additional")) ||
    null
  );
}
